const apiUrl = process.env.REACT_APP_BACKEND_URL;

const normalizeRecommendationPatterns = recommendation => {
  if (!recommendation || !Array.isArray(recommendation.patterns)) return recommendation;

  return {
    ...recommendation,
    patterns: recommendation.patterns.map(pattern => ({
      ...pattern,
      smart_mode: pattern.smart_mode ?? false,
      comfort_mode: pattern.comfort_mode ?? 'neutral',
    })),
  };
};

export const getUserSchedules = async () => {
  const requestOptions = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(`${apiUrl}schedule`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedules;
};

export const getControllerSchedules = async (deviceId, includePatterns) => {
  const requestOptions = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(
    `${apiUrl}schedule/device/${deviceId}/includePatterns=${includePatterns}`,
    requestOptions,
  );

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data;
};

export const getUserSchedulesDetails = async () => {
  const requestOptions = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(`${apiUrl}schedule/details`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedules;
};

export const getSchedule = async scheduleId => {
  const requestOptions = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(`${apiUrl}schedule/${scheduleId}`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedule;
};

export const getRecommendationApi = async devicesId => {
  const token = localStorage.getItem('token');

  const requestOptions = {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  };

  const response = await fetch(`${apiUrl}schedule/recommendation/${devicesId}`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return normalizeRecommendationPatterns(data.recommendation);
};

export const deleteSchedule = async scheduleId => {
  const requestOptions = {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const res = await fetch(`${apiUrl}schedule/delete/${scheduleId}`, requestOptions);

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || 'Delete failed');
  }

  return await res.json();
};

export const saveRecommendationScheduleApi = async (deviceId, scheduleName) => {
  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      scheduleName: scheduleName,
    }),
  };

  const response = await fetch(`${apiUrl}schedule/recommendation/${deviceId}/save`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedule;
};

export const saveRecommendationsForDevicesAPI = async deviceIds => {
  // Ensure deviceIds is an array of numbers
  const numericDeviceIds = Array.isArray(deviceIds) ? deviceIds.map(id => Number(id)) : [];

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      device_ids: numericDeviceIds,
    }),
  };

  const response = await fetch(`${apiUrl}schedule/recommendation/save`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedules || data.schedule;
};

export const saveScheduleApi = async (scheduleData, patterns, deviceIds = null) => {
  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      patterns,
      scheduleData,
      device_ids: deviceIds,
    }),
  };

  const response = await fetch(`${apiUrl}schedule/save`, requestOptions);
  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Save failed');
  }

  return data.schedule;
};

export const activateScheduleById = async scheduleId => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  };

  const response = await fetch(`${apiUrl}schedule/${scheduleId}/activate`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedules;
};

export const activateScheduleForDevicesApi = async (scheduleId, devicesIds) => {
  const requestBody = {
    device_ids: devicesIds,
  };

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  };

  const response = await fetch(`${apiUrl}schedule/${scheduleId}/activate`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedule;
};

export const activateScheduleForSingleDeviceApi = async (scheduleId, deviceId) => {
  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  const response = await fetch(`${apiUrl}schedule/${scheduleId}/${deviceId}`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data.schedule;
};

export const activateRecommendationsApi = async deviceIds => {
  // making sure numeric val
  const numericDeviceIds = Array.isArray(deviceIds) ? deviceIds.map(id => Number(id)) : [];

  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      device_ids: numericDeviceIds,
    }),
  };

  const response = await fetch(`${apiUrl}schedule/recommendation/activate`, requestOptions);

  const data = await response.json();

  // Check for HTTP errors or logical errors in the response
  if (!response.ok) {
    throw new Error(data.error || data.message || 'failed');
  }
  return data;
};
